import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";

const DeviceHistoryLastData = () => {
  const { getUrlByOperation } = useApiUrls();

  const [groupList, setGroupList] = useState([]);
  const [subGroupList, setSubGroupList] = useState([]);

  const [selectedGroupId, setSelectedGroupId] = useState("-1");
  const [selectedSubGroupId, setSelectedSubGroupId] = useState("-1");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [noDataMessage, setNoDataMessage] = useState("");

  const [dataLimit, setDataLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // Modal state for previewing JSON data
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState("");

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  };

  const token = getCookie("token") || getCookie("authToken");
  const userid = getCookie("userid") || getCookie("userId") || "1";

  const formatDate = (val) => {
    if (!val) return "-";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleString();
    } catch (_) {
      return val;
    }
  };

  // Load Groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groupUrl =
          getUrlByOperation("GroupGetDistinctName") ||
          "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/GetDistinct/name";
        const res = await axios.get(groupUrl, {
          headers: {
            accept: "text/plain",
            UserId: userid,
            "X-Authorization": token,
          },
        });
        if (res.data && res.data.isresponse && Array.isArray(res.data.data)) {
          setGroupList(res.data.data);
        } else if (res.data && Array.isArray(res.data.data)) {
          setGroupList(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching group list:", err);
      }
    };

    fetchGroups();
  }, [getUrlByOperation, token, userid]);

  // Handle Group Selection change to fetch SubGroups
  const handleGroupChange = async (e) => {
    const groupId = e.target.value;
    setSelectedGroupId(groupId);

    if (groupId === "-1" || groupId === "" || groupId === "0") {
      setSubGroupList([]);
      setSelectedSubGroupId("-1");
    } else {
      setSelectedSubGroupId("-1");
      try {
        const res = await axios.get(
          `https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/GetDistinct/name/${groupId}`,
          {
            headers: {
              accept: "text/plain",
              UserId: userid,
              "X-Authorization": token,
            },
          }
        );
        if (res.data && res.data.isresponse && Array.isArray(res.data.data)) {
          setSubGroupList(res.data.data);
        } else if (res.data && Array.isArray(res.data.data)) {
          setSubGroupList(res.data.data);
        } else {
          setSubGroupList([]);
        }
      } catch (err) {
        console.error("Error fetching sub-groups:", err);
        setSubGroupList([]);
      }
    }
  };

  const fetchDeviceHistoryData = async () => {
    setLoading(true);
    setMessage("");

    const payload = {
      groupid: selectedGroupId === "" ? -1 : Number(selectedGroupId),
      subgroupid: selectedSubGroupId === "" ? -1 : Number(selectedSubGroupId),
    };

    try {
      const apiUrl =
        getUrlByOperation("DeviceHistoryLastData") ||
        "https://api.dqr-display-tms.eventum.co.in/api/Report/Group/DeviceHistoryLastData";

      const response = await axios.post(apiUrl, payload, {
        headers: {
          accept: "text/plain",
          UserId: userid,
          "X-Authorization": token,
          "Content-Type": "application/json-patch+json",
        },
      });

      if (response.data && response.data.isresponse && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const rows = response.data.data;
        setItems(rows);
        setHasMore(rows.length === Number(dataLimit));
        const approximateTotal =
          count * Number(dataLimit) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
        setNoDataMessage("");
      } else if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const rows = response.data.data;
        setItems(rows);
        setHasMore(rows.length === Number(dataLimit));
        const approximateTotal =
          count * Number(dataLimit) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
        setNoDataMessage("");
      } else {
        setItems([]);
        setHasMore(false);
        setTotalItems(0);
        const errMsg = response.data?.errormsg || response.data?.message || "No matching records found";
        setNoDataMessage(errMsg);
      }
    } catch (error) {
      console.error("Error fetching Device History Last Data:", error);
      setItems([]);
      setHasMore(false);
      setTotalItems(0);
      const errMsg =
        error.response?.data?.errormsg ||
        error.response?.data?.message ||
        error.message ||
        "No matching records found";
      setNoDataMessage(errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceHistoryData();
  }, [getUrlByOperation, token, userid, count, dataLimit]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCount(0);
    fetchDeviceHistoryData();
  };

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const getItemValue = (item) => {
    if (typeof item === "object" && item !== null) {
      return item.id !== undefined ? item.id : item.value !== undefined ? item.value : item.groupid || item.subgroupid || 0;
    }
    return item;
  };

  const getItemLabel = (item) => {
    if (typeof item === "object" && item !== null) {
      return item.value || item.name || item.groupname || item.subgroupname || item.id || "Option";
    }
    return item;
  };

  const openJsonModal = (title, rawData) => {
    setModalTitle(title || "Device Data JSON Preview");
    if (!rawData) {
      setModalContent("No JSON data available.");
    } else if (typeof rawData === "string") {
      try {
        const parsed = JSON.parse(rawData);
        setModalContent(JSON.stringify(parsed, null, 2));
      } catch (_) {
        setModalContent(rawData);
      }
    } else if (typeof rawData === "object") {
      setModalContent(JSON.stringify(rawData, null, 2));
    } else {
      setModalContent(String(rawData));
    }
    setModalOpen(true);
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>Device History Last Data</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                    <li className="breadcrumb-item active">Device History Last Data</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              {/* Filter Selection Form Card */}
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Filter Options</h3>
                    </div>
                    <form onSubmit={handleSearchSubmit}>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label>Group Name</label>
                              <select
                                className="form-control"
                                value={selectedGroupId}
                                onChange={handleGroupChange}
                              >
                                <option value="-1">All</option>
                                {groupList &&
                                  groupList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-5">
                            <div className="form-group">
                              <label>SubGroup Name</label>
                              <select
                                className="form-control"
                                value={selectedSubGroupId}
                                onChange={(e) => setSelectedSubGroupId(e.target.value)}
                              >
                                <option value="-1">All</option>
                                {selectedGroupId !== "-1" && selectedGroupId !== "" && selectedGroupId !== "0" &&
                                  subGroupList &&
                                  subGroupList.map((item, idx) => (
                                    <option key={idx} value={getItemValue(item)}>
                                      {getItemLabel(item)}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-2 d-flex align-items-end mb-3">
                            <button type="submit" className="btn btn-info btn-block" disabled={loading}>
                              {loading ? "Loading..." : "Search"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {/* Data Table Card */}
              <div className="row">
                <div className="col-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">Device History Last Data List</h3>
                    </div>
                    <div className="card-body table-responsive p-0">
                      {loading ? (
                        <div className="text-center p-4">Loading...</div>
                      ) : items && items.length > 0 ? (
                        <table className="table table-hover text-nowrap">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>ID</th>
                              <th>Group Name</th>
                              <th>SubGroup Name</th>
                              <th>POS Name</th>
                              <th>Merchant ID</th>
                              <th>Store ID</th>
                              <th>Serial No</th>
                              <th>Created Date</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((row, index) => (
                              <tr key={row.id || index}>
                                <td>{index + 1}</td>
                                <td>{row.id || "-"}</td>
                                <td>{row.groupid === -1 ? "All" : row.groupname || row.groupid || "-"}</td>
                                <td>{row.subgroupid === -1 ? "All" : row.subgroupname || row.subgroupid || "-"}</td>
                                <td>{row.posname || "-"}</td>
                                <td>{row.merchantid || "-"}</td>
                                <td>{row.storeid || "-"}</td>
                                <td>{row.currentserialno || row.serialno || "-"}</td>
                                <td>{formatDate(row.historycreateddate)}</td>
                                <td>
                                  <button
                                    className="btn btn-info btn-sm"
                                    title="Preview JSON"
                                    onClick={() => openJsonModal(`JSON Preview - POS: ${row.posname || row.id}`, row.data)}
                                  >
                                    <i className="fas fa-eye mr-1"></i> Preview JSON
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-center p-4 text-muted font-weight-bold">
                          {noDataMessage || "No matching records found"}
                        </div>
                      )}
                    </div>
                    {items && items.length > 0 && (
                      <div className="card-footer clearfix">
                        <Pagination
                          currentPage={count + 1}
                          hasMore={hasMore}
                          onPageChange={handlePageChange}
                          totalItems={totalItems}
                          itemsPerPage={dataLimit}
                          onItemsPerPageChange={handleItemsPerPageChange}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* JSON Preview Modal */}
      {modalOpen && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header card-info">
                <h5 className="modal-title font-weight-bold">
                  <i className="fas fa-eye mr-2"></i> {modalTitle}
                </h5>
                <button type="button" className="close text-white" onClick={() => setModalOpen(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body p-3">
                <pre style={{
                  backgroundColor: "#272822",
                  color: "#f8f8f2",
                  padding: "15px",
                  borderRadius: "5px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  fontSize: "13px",
                  fontFamily: "monospace"
                }}>
                  {modalContent}
                </pre>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {message && messageType === "success" && (
        <SuccessMessage message={message} onClose={() => setMessage("")} />
      )}
      {message && messageType === "error" && (
        <FailedMessage message={message} onClose={() => setMessage("")} />
      )}
    </>
  );
};

export default DeviceHistoryLastData;
